import { useState, useEffect, useCallback, useMemo, useRef } from 'react'
import { supabase } from '@/lib/supabase'
import { subscriberChannel, subscribeToConversation } from '@/lib/realtime'
import { useAuthStore } from '@/stores/auth'
import { logger } from '@/lib/logger'

/**
 * Nøkkelen en samtale kjennes på — HELE veien, fra ruta til raden i databasen.
 *
 * `MessagesPage` ruter på den (`/messages/:eventId`), `chat_reads.chat_key`
 * lagrer den, og `get_chat_unread_counts()` grupperer på den. Én streng, tre
 * lag, definert ett sted: da kan de ikke drive fra hverandre.
 */
export function conversationKey(conv: Pick<Conversation, 'isSubEvent' | 'subEventId' | 'eventId'>): string {
  return conv.isSubEvent && conv.subEventId ? `sub:${conv.subEventId}` : conv.eventId
}

export interface Conversation {
  eventId: string
  eventTitle: string
  isSubEvent: boolean
  subEventId?: string
  subEventStartDate?: string | null
  lastMessage?: { text: string; createdAt: string; userId: string }
  unreadCount: number
  chatEnabled: boolean
  /** Har denne samtalen minst én melding? Målt uavhengig av forhåndsvisnings-vinduet. */
  hasMessages: boolean
  /** Arrangementets status — avgjør om en TOM samtale er verdt en linje i lista. */
  eventStatus?: string | null
  /**
   * Samtaleraden bak dette rommet, når den finnes — og den finnes bare når
   * rommet har minst én melding, fordi `ensure_event_conversation()` lager den
   * ved FØRSTE sending. Nullbar av nettopp den grunnen, ikke av slurv.
   *
   * Den bæres her og ikke i egen tilstand fordi den er en egenskap ved
   * samtalen, og fordi abonnementsløkka under skal kunne utledes av lista på
   * nøyaktig samme måte som i `useDirectConversations`.
   */
  conversationId?: string
  /** Emnets epoke (S2). Endres den, byttes abonnementet ut. */
  membershipEpoch?: number
}

/**
 * En tom samtale fortjener en linje i innboksen bare hvis noen realistisk skal
 * begynne å skrive i den. Et utkast har ingen deltakere ennå, et fullført eller
 * avlyst arrangement har ingen igjen. Begge kan fortsatt startes fra selve
 * arrangementssiden; de skal bare ikke stå i lista og se ut som en samtale.
 *
 * Dette er hele grunnen til at innboksen kunne vise 15 kanaler for et ferdig
 * arrangement fra 2025 uten en eneste melding i noen av dem.
 */
const DORMANT_STATUSES = new Set(['draft', 'completed', 'cancelled'])

export interface ConversationGroup {
  eventId: string
  eventTitle: string
  mainConv: Conversation          // the event-level chat
  subConvs: Conversation[]        // sub-event chats nested under this event
}

/**
 * ⚠️ ÉN INSTANS, og det er ikke en optimalisering.
 *
 * Denne het `useInboxConversations` og ble kalt tre steder — sidemenyen,
 * bunnmenyen og `/messages`. Hver av dem fikk sin egen tilstand, så da
 * `markRead` nullstilte tallet i den ene, sto merket igjen i de to andre. Det er
 * den feilen David meldte: «meldinger … fjernes når noen har sett dem».
 *
 * Nå eier `UnreadProvider` den ene instansen, og alle tre leser den. Kall den
 * ikke direkte — bruk `useUnread()`. `check-realtime-topic.mjs` holder linja.
 */
export function useInboxConversationsSource() {
  const [conversations, setConversations] = useState<Conversation[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const user = useAuthStore((s) => s.user)

  const fetchConversations = useCallback(async () => {
    setError(null)

    if (!user) {
      setLoading(false)
      return
    }

    try {
      // Eide arrangementer + de jeg har svart ja til + de jeg har en ROLLE på.
      //
      // Rollene manglet. `notification_recipients_for_event` varsler
      // event_roles-innehavere (admin/manager/editor/owner) om hver
      // chat-melding, men innboksen kjente dem ikke: en medadministrator som
      // ikke selv hadde svart «ja» fikk varsel om en samtale som ikke fantes
      // noe sted i grensesnittet hans. 16 rolleinnehavere står slik i dag.
      const [ownedRes, rsvpRes, roleRes] = await Promise.all([
        supabase
          .from('events')
          .select('id, title, chat_enabled, sub_event_chat_enabled, is_offer_mode, status')
          .eq('owner_id', user.id),
        // `status` holds 'yes' / 'no' / 'maybe' — never 'attending'. Checked
        // against production: 188 'yes', 7 'no', 1 'maybe', 0 'attending'. This
        // filter therefore matched nothing, so the inbox only ever listed
        // events you OWN; an event you were merely attending had no
        // conversation, however much chat it had.
        supabase
          .from('rsvps')
          .select('event_id, events(id, title, chat_enabled, sub_event_chat_enabled, is_offer_mode, status)')
          .eq('user_id', user.id)
          .eq('status', 'yes'),
        supabase
          .from('event_roles')
          .select('event_id, events(id, title, chat_enabled, sub_event_chat_enabled, is_offer_mode, status)')
          .eq('user_id', user.id)
          // Operator access to every tenant is not an inbox of every tenant's chats.
          .eq('hidden', false),
      ])

      if (ownedRes.error) {
        setError(ownedRes.error.message)
        return
      }
      if (rsvpRes.error) {
        setError(rsvpRes.error.message)
        return
      }
      if (roleRes.error) {
        setError(roleRes.error.message)
        return
      }

      // Merge and deduplicate events
      const eventMap = new Map<string, {
        id: string
        title: string
        chat_enabled: boolean
        sub_event_chat_enabled: boolean
        status: string | null
      }>()

      for (const e of ownedRes.data ?? []) {
        if (e.is_offer_mode) continue
        eventMap.set(e.id, {
          id: e.id,
          title: e.title,
          chat_enabled: e.chat_enabled,
          sub_event_chat_enabled: e.sub_event_chat_enabled,
          status: e.status,
        })
      }

      // Samme embed-form fra `rsvps` og `event_roles`, samme dedup.
      type EmbeddedEvent = {
        id: string
        title: string
        chat_enabled: boolean
        sub_event_chat_enabled: boolean
        is_offer_mode: boolean | null
        status: string | null
      }
      for (const row of [...(rsvpRes.data ?? []), ...(roleRes.data ?? [])]) {
        const e = row.events as unknown as EmbeddedEvent | null
        if (!e || e.is_offer_mode) continue
        if (!eventMap.has(e.id)) {
          eventMap.set(e.id, {
            id: e.id,
            title: e.title,
            chat_enabled: e.chat_enabled,
            sub_event_chat_enabled: e.sub_event_chat_enabled,
            status: e.status,
          })
        }
      }

      const eventIds = Array.from(eventMap.keys())
      if (eventIds.length === 0) {
        setConversations([])
        setLoading(false)
        return
      }

      // Fetch sub-events for events with sub_event_chat_enabled
      const enabledSubEventParents = eventIds.filter(
        (id) => eventMap.get(id)?.sub_event_chat_enabled
      )
      const subEventsRes = enabledSubEventParents.length > 0
        ? await supabase
            .from('sub_events')
            .select('id, title, event_id, chat_enabled, start_date')
            .in('event_id', enabledSubEventParents)
        : { data: [], error: null }

      if (subEventsRes.error) {
        setError(subEventsRes.error.message)
        return
      }

      // Samtaleradene bak rommene — id og epoke, som er alt abonnementet trenger.
      //
      // Hentes som en EGEN, smal spørring og ikke som en embed på `messages`:
      // et rom uten meldinger har ingen melding å henge en embed på, og det er
      // nettopp de rommene vi må kunne se. RLS avgrenser den til rom brukeren
      // er medlem av — `conversations_select` → `can_read_conversation` →
      // `is_event_member` for arrangementsrom, altså den avledede formen.
      const { data: convRows, error: convError } = await supabase
        .from('conversations')
        .select('id, event_id, sub_event_id, kind, membership_epoch')
        .in('event_id', eventIds)
        .in('kind', ['event', 'sub_event'])

      // ⚠️ En feil her skal IKKE ta ned innboksen. Uten disse radene mangler
      //    lista sanntid, ikke innhold — og en innboks uten oppdatering er
      //    langt mindre galt enn en tom side. Samme regel som tellingen under.
      if (convError) logger.error('inbox conversation wires failed:', convError)

      const convByKey = new Map<string, { id: string; epoch: number }>()
      for (const c of convRows ?? []) {
        if (!c.event_id) continue
        const key = c.sub_event_id ? `sub:${c.sub_event_id}` : c.event_id
        convByKey.set(key, { id: c.id, epoch: c.membership_epoch ?? 0 })
      }

      // De 200 nyeste — til FORHÅNDSVISNINGEN, og bare den.
      //
      // ⚠️ Uleste ble tidligere talt her, i dette vinduet, mot et «sist
      // sett»-tidspunkt som lå i en ref i minnet. To feil i én løkke: reffen var
      // tom ved hver last, så hver melding fra en annen ble talt om igjen og
      // merket kunne aldri bli borte — og et vindu på 200 meldinger på tvers av
      // ALLE arrangementene dine begynner stille å underrapportere så snart du
      // har flere enn det. Tellingen skjer nå i databasen, mot en lagret
      // lesekvittering. Se `get_chat_unread_counts` og `chat_reads`.
      //
      // ⛔ LESER `messages`, IKKE `chats` — flyttet i S3, sammen med skriveveien.
      //    Arrangementsrom skriver til `messages` fra 22.08.2026. `chats` står
      //    igjen som tabell for en fane som er bufret fra før utrullingen, men
      //    den vokser ikke lenger — så en innboks som fortsatt leste den ville
      //    vist gårsdagens siste melding under hver samtale, uten en eneste feil
      //    noe sted. En stille løgn i lista er dyrere enn en rød boks.
      const { data: recentChats, error: chatsError } = await supabase
        .from('messages')
        .select('body, created_at, sender_id, kind, conversations!inner(event_id, sub_event_id, kind)')
        .in('conversations.event_id', eventIds)
        .in('conversations.kind', ['event', 'sub_event'])
        .is('deleted_at', null)
        .neq('kind', 'system')
        .order('created_at', { ascending: false })
        .limit(200)

      if (chatsError) {
        setError(chatsError.message)
        return
      }

      // Uleste per samtale, talt av databasen og avgrenset av RLS på `chats`.
      // En feil her skal IKKE ta ned innboksen: lista er fortsatt lesbar uten
      // tall, og et merke som mangler er mindre galt enn en tom side.
      const { data: unreadRows, error: unreadError } = await supabase.rpc('get_chat_unread_counts')
      if (unreadError) logger.error('get_chat_unread_counts failed:', unreadError)
      const unreadByKey = new Map<string, number>()
      for (const row of unreadRows ?? []) unreadByKey.set(row.chat_key, row.unread_count)

      // «Finnes det meldinger her?» kan IKKE avledes av de 200 nyeste: en
      // samtale som har ligget stille lenge faller ut av vinduet og ville blitt
      // regnet som tom. Egen, smal spørring — to uuid-kolonner, RLS-avgrenset
      // til arrangementene brukeren hører til.
      const { data: presenceRows, error: presenceError } = await supabase
        .from('messages')
        .select('conversations!inner(event_id, sub_event_id, kind)')
        .in('conversations.event_id', eventIds)
        .in('conversations.kind', ['event', 'sub_event'])
        .is('deleted_at', null)
        .neq('kind', 'system')

      if (presenceError) {
        setError(presenceError.message)
        return
      }

      const eventsWithMessages = new Set<string>()
      const subEventsWithMessages = new Set<string>()
      for (const row of presenceRows ?? []) {
        const c = row.conversations
        // `event_id` er nullbar i skjemaet fordi en DM ikke har noe arrangement.
        // Her er den aldri null — spørringen filtrerer på kind — men typen vet
        // det ikke, og en `!` ville vært en påstand i stedet for en kontroll.
        if (!c?.event_id) continue
        if (c.sub_event_id) subEventsWithMessages.add(c.sub_event_id)
        else eventsWithMessages.add(c.event_id)
      }

      // Group last message by event_id (no sub_event) and sub_event_id
      const lastByEvent = new Map<string, { text: string; createdAt: string; userId: string }>()
      const lastBySubEvent = new Map<string, { text: string; createdAt: string; userId: string }>()

      for (const msg of recentChats ?? []) {
        const c = msg.conversations
        if (!c?.event_id) continue
        const key = c.sub_event_id ?? c.event_id
        const targetMap = c.sub_event_id ? lastBySubEvent : lastByEvent

        if (!targetMap.has(key)) {
          targetMap.set(key, {
            // `body` er nullbar på en gravstein. Slettede meldinger er alt
            // filtrert bort over, men typen vet det ikke — og en `??` her er
            // billigere enn en boks som viser «null» i innbokslista.
            text: msg.body ?? '',
            createdAt: msg.created_at,
            userId: msg.sender_id ?? '',
          })
        }
      }

      // Build conversation list
      const result: Conversation[] = []

      // Event-level conversations
      for (const [id, event] of eventMap) {
        result.push({
          eventId: id,
          eventTitle: event.title,
          isSubEvent: false,
          lastMessage: lastByEvent.get(id),
          unreadCount: unreadByKey.get(id) ?? 0,
          chatEnabled: event.chat_enabled,
          hasMessages: eventsWithMessages.has(id),
          eventStatus: event.status,
          conversationId: convByKey.get(id)?.id,
          membershipEpoch: convByKey.get(id)?.epoch,
        })
      }

      // Sub-event conversations
      for (const sub of (subEventsRes.data ?? []).filter((s) => s.chat_enabled !== false)) {
        const parent = eventMap.get(sub.event_id)
        if (!parent) continue
        result.push({
          eventId: sub.event_id,
          eventTitle: sub.title ?? 'Sub-event',
          isSubEvent: true,
          subEventId: sub.id,
          subEventStartDate: sub.start_date,
          lastMessage: lastBySubEvent.get(sub.id),
          unreadCount: unreadByKey.get(`sub:${sub.id}`) ?? 0,
          chatEnabled: parent.chat_enabled && parent.sub_event_chat_enabled,
          hasMessages: subEventsWithMessages.has(sub.id),
          eventStatus: parent.status,
          conversationId: convByKey.get(`sub:${sub.id}`)?.id,
          membershipEpoch: convByKey.get(`sub:${sub.id}`)?.epoch,
        })
      }

      // Sort: unread first, then by most recent message
      result.sort((a, b) => {
        if (a.unreadCount > 0 && b.unreadCount === 0) return -1
        if (b.unreadCount > 0 && a.unreadCount === 0) return 1
        const aTime = a.lastMessage?.createdAt ?? ''
        const bTime = b.lastMessage?.createdAt ?? ''
        return bTime.localeCompare(aTime)
      })

      setConversations(result)
    } catch (err) {
      logger.error('Failed to fetch inbox conversations:', err)
      setError(err instanceof Error ? err.message : 'Unknown error')
    } finally {
      setLoading(false)
    }
  }, [user])

  useEffect(() => {
    fetchConversations()
  }, [fetchConversations])

  /* ────────────────────────────────────────────────────────────────────────
   * Sanntid — ett abonnement per rom, på kringkastingsemnet
   * ────────────────────────────────────────────────────────────────────────
   *
   * ⛔ Her sto det ett `postgres_changes`-abonnement på `messages`, og det kunne
   *    aldri motta noe. Postgres CDC leverer bare tabeller som ligger i
   *    publikasjonen, og `messages` ligger ikke i NOEN publikasjon — målt
   *    22.08.2026 mot produksjon, uten filter på publikasjonsnavn. Merket i
   *    sidemenyen, i bunnmenyen og på `/messages` kommer alt herfra, så alle
   *    tre sto stille til noe annet framtvang en ny henting.
   *
   *    Kommentaren som sto her var verre enn feilen: den beskrev kostnaden ved
   *    å bli vekket av andres DM-er som en KJENT begrensning vi tålte ved 22
   *    meldinger. Hver setning beskrev en kostnad som aldri kunne påløpe, fordi
   *    hendelsen aldri kom. «Kjent begrensning» er merkelappen som får neste
   *    leser til å slutte å se etter.
   *
   * Formen er den S2 beviste på tråder: én kanal per samtale, delt og
   * ref-tellet av `subscribeToConversation`, med epoken i nøkkelen. Epoken er
   * ikke pynt — blir du fjernet, flytter rommet seg til et emne du ikke slipper
   * inn i, og abonnementet byttes ut sammen med raden.
   */
  const wireKeys = useMemo(
    () =>
      conversations
        .filter((c) => c.conversationId)
        .map((c) => `${c.conversationId}:${c.membershipEpoch ?? 0}`)
        // To rader kan peke på SAMME samtale (et rom står i lista både som seg
        // selv og under forelderen sin i grupperingen). Uten dedup ville vi
        // abonnert to ganger og hentet to ganger per melding.
        .filter((key, i, all) => all.indexOf(key) === i)
        .sort()
        .join(','),
    [conversations]
  )

  /* Refetch-en byttes ut ved hver render av `fetchConversations`. Lå den i
     avhengighetslista under, ville hele abonnementssettet blitt revet ned og
     bygget opp igjen for hver henting — og en kanal som melder seg av og på
     mister rammene imellom. Ref-en holder den ferskeste uten å røre effekten. */
  const refetchRef = useRef(fetchConversations)
  useEffect(() => {
    refetchRef.current = fetchConversations
  }, [fetchConversations])

  useEffect(() => {
    if (!user || !wireKeys) return
    const unsubs = wireKeys.split(',').map((key) => {
      const at = key.lastIndexOf(':')
      return subscribeToConversation(key.slice(0, at), Number(key.slice(at + 1)), () => {
        void refetchRef.current()
      })
    })
    return () => unsubs.forEach((u) => u())
  }, [user, wireKeys])

  /* Rommet som nettopp ble født.
   *
   * Løkka over kan bare abonnere på rom som HAR en samtalerad, og den raden
   * lages først ved første sending (`ensure_event_conversation`). Målt
   * 22.08.2026: 2 av 6 levende rom hadde en rad, og 0 av 91 underarrangementer
   * — så uten dette ville melding #1 i de aller fleste rom ikke rørt merket.
   *
   * `conversations` ble lagt i publikasjonen for nettopp dette (migrasjon
   * `20260822_messaging_s3_1_inbox_live_wire`). Den innsettes én gang per rom,
   * ikke én gang per melding, som er hele grunnen til at argumentet mot å
   * publisere `messages` (hull #9) ikke gjelder her. Refetchen plukker opp
   * emnet, og alt etter melding #1 går gjennom løkka over.
   */
  useEffect(() => {
    if (!user) return
    const channel = subscriberChannel('inbox-conversation-born')
      .on(
        'postgres_changes',
        { event: 'INSERT', schema: 'public', table: 'conversations' },
        () => {
          void refetchRef.current()
        }
      )
      .subscribe()

    return () => {
      supabase.removeChannel(channel)
    }
  }, [user])

  /**
   * «Jeg har sett denne samtalen», skrevet ned.
   *
   * Den satte tidligere et tidspunkt i en ref og nullstilte tallet i sin egen
   * kopi av lista. Ingen av delene overlevde noe som helst: reffen døde ved
   * neste last, og de to andre instansene av hooken — sidemenyen og bunnmenyen
   * — hørte aldri om det. Nå er skrivingen en rad i `chat_reads`, og det finnes
   * bare én instans å oppdatere.
   *
   * Optimistisk først, så skriv: merket skal forsvinne i samme frame som
   * trykket. Feiler skrivingen, henter vi sannheten tilbake i stedet for å la
   * lista påstå noe databasen aldri gikk med på — samme regel som
   * `useNotifications.guard`.
   */
  const markRead = useCallback(
    async (chatKey: string) => {
      if (!user) return
      const target = conversations.find((c) => conversationKey(c) === chatKey)
      setConversations((prev) =>
        prev.map((c) => (conversationKey(c) === chatKey ? { ...c, unreadCount: 0 } : c))
      )
      // Ukjent nøkkel = ingen rad å knytte kvitteringen til. Skjer bare før
      // lista har landet; neste henting tar den.
      if (!target) return

      // ⚠️ `last_read_at` sendes IKKE. Den stemples av basen
      // (`chat_reads_stamp_now`, migrasjon 20260815_chat_reads_server_clock),
      // fordi den sammenlignes med `chats.created_at` — og DEN settes av
      // serverens `now()`, aldri av klienten. To klokker i én sammenligning er
      // en feil som venter på en mobil med feil tid: går den for fort, blir
      // meldinger som ennå ikke er skrevet stemplet som lest, og varselet kommer
      // aldri. Én klokke, og det er serverens.
      const { error: writeError } = await supabase.from('chat_reads').upsert(
        {
          user_id: user.id,
          event_id: target.eventId,
          sub_event_id: target.isSubEvent ? (target.subEventId ?? null) : null,
          chat_key: chatKey,
        },
        { onConflict: 'user_id,chat_key' }
      )
      if (writeError) {
        logger.error('Failed to mark conversation read:', writeError)
        void fetchConversations()
      }
    },
    [user, conversations, fetchConversations]
  )

  // Build grouped structure: one group per event, with sub-events nested.
  //
  // Tre filtre, i denne rekkefølgen — de svarer på hvorfor sidemenyen var full
  // av kanaler ingen hadde skrevet i:
  //   1. chat av på arrangementet  → ingen gruppe i det hele tatt.
  //   2. et underpunkt uten meldinger → ingen linje. Kanalen finnes fortsatt og
  //      kan startes fra arrangementets chat-modal; den skal bare ikke stå her
  //      og late som om det er en samtale. Ett arrangement med 14 programpunkter
  //      lagde 14 slike linjer, alle tomme.
  //   3. et tomt hovedspor på et utkast/fullført/avlyst arrangement → ingen
  //      linje, med mindre et av underpunktene faktisk HAR meldinger.
  //
  // `conversations` beholder alt: en direktelenke til /messages/<id> åpner
  // fortsatt samtalen selv om den ikke står i lista.
  const groups: ConversationGroup[] = conversations
    .filter((c) => !c.isSubEvent && c.chatEnabled !== false)
    .map((mainConv) => ({
      eventId: mainConv.eventId,
      eventTitle: mainConv.eventTitle,
      mainConv,
      subConvs: conversations
        .filter((c) => c.isSubEvent && c.eventId === mainConv.eventId && c.hasMessages)
        .sort((a, b) => (a.subEventStartDate ?? '').localeCompare(b.subEventStartDate ?? '')),
    }))
    .filter((g) => {
      const dormant = DORMANT_STATUSES.has(g.mainConv.eventStatus ?? '')
      return !dormant || g.mainConv.hasMessages || g.subConvs.length > 0
    })

  /**
   * Tallet menyene tegner — summert over det innboksen FAKTISK VISER.
   *
   * Ikke over `conversations`, som beholder alt. Filtrene rett over kan skjule
   * en samtale som har uleste: skru av chatten på et arrangement det ligger
   * ulest i, og raden forsvinner fra lista mens tallet blir stående. Før var
   * det en skjønnhetsfeil, fordi tellingen uansett ble regnet på nytt ved hver
   * last. Nå ligger lesekvitteringen i databasen, så et merke uten en rad å
   * trykke på ville blitt stående for ALLTID — det er ingen vei i
   * grensesnittet til å nullstille en samtale du ikke ser.
   *
   * Målt 15.08.2026: ingen rad i produksjon står slik i dag (22 chatmeldinger,
   * alle på arrangementer med `chat_enabled = true`). Dette lukker den før den
   * finnes, fordi den ellers ikke ville vist seg som et krasj, men som et rødt
   * merke ingen kan bli kvitt.
   */
  const unreadTotal = groups.reduce(
    (sum, g) => sum + g.mainConv.unreadCount + g.subConvs.reduce((s, c) => s + c.unreadCount, 0),
    0
  )

  return { conversations, groups, unreadTotal, loading, markRead, error, refetch: fetchConversations }
}
